import { useState } from 'react';
import { Banner, Button, Link, Table, TableActiveColumnFilters, TableHeader, TableProvider } from '@optimus-web/core';
import { useResolveRoute } from '@mews/routing';
import { TextKey, ArticlePath, createGuideLink, RouteName } from '@mews-commander/enums';
import { LocalizedText, useUrlSyncedTableState } from '@mews/framework';
import { ScimProvisioningStatus, useRepositoryQuery, UserGroup } from '@mews-commander/data-access';
import { openInBrowserTab } from '@mews-commander/utils';
import { useUserGroupsQuery } from '../../queries/useUserGroupsQuery';
import { UserGroupDetailsPanel } from '../UserGroupDetailsPanel';
import { useColumns } from './tableMapping';

export const UserGroupsList = () => {
    const [selectedDetailsGroup, setSelectedDetailsGroup] = useState<UserGroup | null>(null);

    const columns = useColumns({ onOpenDetails: (group: UserGroup) => setSelectedDetailsGroup(group) });

    const [tableState, setTableState] = useUrlSyncedTableState<UserGroup>({
        columns,
        pageSize: 50,
    });

    const { data: userGroups, isFetching } = useUserGroupsQuery(tableState);
    const { data: scimConfiguration } = useRepositoryQuery('scimConfiguration', 'get', {});

    const totalRowCount = userGroups?.totalRowCount || 0;

    const emptyStateGuideLink = createGuideLink(ArticlePath.UserGroupsDetailPanel);

    return (
        <>
            {scimConfiguration?.provisioningStatus === ScimProvisioningStatus.Disabled && <ScimDisabledBanner />}
            <TableProvider state={tableState} onStateChange={setTableState} columns={columns}>
                <TableHeader
                    title={<LocalizedText textKey={TextKey.GroupPlural} />}
                    globalSearch={{ placeholder: <LocalizedText textKey={TextKey.UserGroupsSearchHint} /> }}
                />

                <TableActiveColumnFilters />

                <Table
                    uniqueRowId="id"
                    data={userGroups?.data || []}
                    amountOfRows={totalRowCount}
                    loading={isFetching}
                    fixedFirstColumn
                    emptyState={{
                        primaryText: <LocalizedText textKey={TextKey.NoUserGroupsFound} />,
                        secondaryText: <LocalizedText textKey={TextKey.NoUserGroupsFoundHint} />,
                        action: (
                            <Button onClick={() => openInBrowserTab(emptyStateGuideLink)} type="submit">
                                <LocalizedText textKey={TextKey.LearnHowToSyncGroups} />
                            </Button>
                        ),
                    }}
                />
            </TableProvider>
            <UserGroupDetailsPanel group={selectedDetailsGroup} onClose={() => setSelectedDetailsGroup(null)} />
        </>
    );
};

const ScimDisabledBanner = () => {
    const resolveRoute = useResolveRoute();

    return (
        <Banner
            variant="warning"
            primaryText={<LocalizedText textKey={TextKey.UserGroupsScimDeactivatedBannerPrimaryText} />}
            secondaryText={
                <>
                    <Link variant="basic" href={resolveRoute(RouteName.SecuritySettings)}>
                        <LocalizedText textKey={TextKey.UserGroupsScimDeactivatedBannerLink} />
                    </Link>
                </>
            }
        />
    );
};
